import type { BaseModelAttributes } from './base';
import { TaskStatus, TaskStatusLabels } from './taskStatus';
import { I18nLabels } from './common';
import { Locale } from './locale';

// 使用统一的 TaskStatus
export type MusicGenerationStatus = TaskStatus;
export const MusicGenerationStatus = TaskStatus;

// 自定义状态标签
export const MusicGenerationStatusLabels: I18nLabels<MusicGenerationStatus> = {
  ...TaskStatusLabels,
  [TaskStatus.PROCESSING]: { [Locale.ZH_CN]: '生成中', [Locale.EN_US]: 'Generating' },
};

// 生成模式
export type MusicGenerationMode = 'simple' | 'professional';

export interface IMusicGeneration extends BaseModelAttributes {
  title?: string;              // 用户自定义标题（可选）
  prompt: string;              // tags: 音乐风格描述
  lyrics?: string;             // 歌词（可选）
  duration: number;            // 时长（秒）
  bpm?: number;               // 节拍（可选）
  language?: string;           // 语言（可选，如 'en', 'zh'）
  keyscale?: string;           // 调性（可选，如 'C major', 'A minor'）
  generationMode: MusicGenerationMode; // 生成模式
  timesignature?: string;      // 拍号（可选，如 '4/4'）
  referenceAudioUrl?: string;  // 参考音频 URL（用于音色/风格迁移）
  modelId: string;             // 使用的模型 ID
  status: MusicGenerationStatus;
  taskId?: string;             // 外部任务 ID
  resultAudioUrl?: string;     // 生成的音频 URL
  resultMessage?: string;      // 结果消息或错误信息
  userId: string;              // 用户 ID
  musicType?: string;          // 音乐类型: generation | editing
  editingType?: string;        // 编辑子类型: cover | repaint | extract | complete
  srcAudioUrl?: string;        // 源音频 URL（编辑任务输入）
  repaintingStart?: number;    // 重绘起始时间（秒）
  repaintingEnd?: number;      // 重绘结束时间（秒）
  audioCoverStrength?: number; // 翻唱音色影响力 0.0~1.0
  coverNoiseStrength?: number; // 翻唱噪声强度 0.0~1.0
  trackName?: string;          // 提取音轨名称: vocals | accompaniment
  rvcModelId?: string;         // Applio RVC 模型 ID（song_cover 专用）
  pitch?: number;              // 音调偏移 -24~24（song_cover 专用）
  indexRate?: number;          // index 影响强度 0~1（song_cover 专用）
  secondaryResultAudioUrl?: string; // 分离第二条音轨 URL（separate 专用）
  submittedAt?: Date;          // 提交到外部 API 的时间
  completedAt?: Date;          // 完成时间
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateMusicGenerationRequest {
  prompt: string;
  duration: number;
  modelId: string;
  lyrics?: string;
  bpm?: number;
  language?: string;
  keyscale?: string;
  generationMode?: MusicGenerationMode;
  timesignature?: string;
  referenceAudioUrl?: string;
}

export interface UpdateMusicGenerationRequest {
  prompt?: string;
  duration?: number;
  modelId?: string;
  lyrics?: string;
  bpm?: number;
  language?: string;
  keyscale?: string;
  generationMode?: MusicGenerationMode;
  timesignature?: string;
  referenceAudioUrl?: string;
  status?: MusicGenerationStatus;
  taskId?: string;
  resultAudioUrl?: string;
  resultMessage?: string;
  submittedAt?: Date;
  completedAt?: Date;
}

export interface MusicGenerationQuery {
  search?: string;             // 搜索 prompt 内容
  userId?: string;             // 按用户筛选
  status?: MusicGenerationStatus;  // 按状态筛选
  modelId?: string;            // 按模型筛选
  musicType?: string;          // 按类型筛选: generation | editing
}

/**
 * 更新音乐标题请求
 */
export interface UpdateMusicTitleRequest {
  title: string;
}

/**
 * 器乐/人声平衡选项（与后台 InstrumentalityBalance 对齐）
 */
export type InstrumentalityBalance =
  | 'pure_instrumental'
  | 'very_instrumental'
  | 'balanced'
  | 'vocal_focused'
  | 'vocals_only';

/**
 * 歌词生成请求
 */
export interface GenerateLyricsRequest {
  style: string;
  instrumentalityBalance?: InstrumentalityBalance;
  language?: 'zh' | 'en' | 'auto';
  extraGuidance?: string;
}

/**
 * 歌词生成响应（同时返回优化后的 caption 和歌词）
 */
export interface GenerateLyricsResponse {
  /** 优化后的 caption（可回写到 prompt 字段） */
  caption: string;
  /** 生成的歌词（含 ACE-Step 结构标签） */
  lyrics: string;
  /** 建议的 BPM（可选） */
  suggestedBpm?: number;
  /** 建议的调性（可选） */
  suggestedKey?: string;
  /** 检测到的语言 */
  detectedLanguage: 'zh' | 'en';
}
